/**
 * Google Authentication Service for PIM System
 * 
 * Handles Google OAuth integration with the backend API.
 * Implements Requirements 1.3, 1.6 for Google SSO functionality.
 */

import api from '../api';

// Types for Google OAuth responses
export interface GoogleAuthResponse {
  access_token: string;
  token_type: string;
  user: {
    id: number;
    username: string;
    email: string;
    role: string;
    is_active: boolean;
    auth_provider: string;
    google_id?: string;
    profile_picture_url?: string;
    created_at: string;
    last_login_at?: string;
  };
}

export interface GoogleAuthUrlResponse {
  authorization_url: string;
  redirect_uri: string;
}

export interface GoogleLogoutResponse {
  logout_url: string;
  message: string;
}

export interface GoogleAuthError {
  detail: string;
  status_code?: number;
}

/**
 * Google Authentication Service Class
 * 
 * Provides methods for Google OAuth integration including:
 * - Authorization URL generation
 * - OAuth callback handling
 * - Session management
 * - Error handling
 */
export class GoogleAuthService {
  private static instance: GoogleAuthService;
  private isInitialized: boolean = false;

  private constructor() {}

  /**
   * Get singleton instance of GoogleAuthService
   */
  public static getInstance(): GoogleAuthService {
    if (!GoogleAuthService.instance) {
      GoogleAuthService.instance = new GoogleAuthService();
    }
    return GoogleAuthService.instance;
  }

  /**
   * Retry a function with exponential backoff.
   * Handles brief backend restarts (e.g. uvicorn --reload).
   */
  private async withRetry<T>(fn: () => Promise<T>, maxAttempts: number = 3, delayMs: number = 1000): Promise<T> {
    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        const isNetworkError = error.code === 'ERR_NETWORK' || error.message === 'Network Error';
        const isEmptyResponse = error.code === 'ECONNRESET' || error.message?.includes('ERR_EMPTY_RESPONSE');
        
        if ((isNetworkError || isEmptyResponse) && attempt < maxAttempts) {
          console.log(`Google OAuth: Retry attempt ${attempt}/${maxAttempts} after network error (backend may be restarting)...`);
          await new Promise(resolve => setTimeout(resolve, delayMs * attempt));
          continue;
        }
        throw error;
      }
    }
    throw new Error('Max retry attempts reached');
  }

  /**
   * Initialize Google OAuth service
   * 
   * Validates: Requirements 1.3 - Google SSO initialization
   */
  public async initializeGoogleAuth(): Promise<void> {
    try {
      // Verify Google OAuth is configured on backend (with retry for backend restarts)
      const response = await this.withRetry(
        () => api.get<GoogleAuthUrlResponse>('/auth/google/url'),
        3,
        1500
      );
      
      if (response.data.authorization_url) {
        this.isInitialized = true;
        console.log('Google OAuth service initialized successfully');
      } else {
        throw new Error('Invalid Google OAuth configuration');
      }
    } catch (error: any) {
      console.error('Failed to initialize Google OAuth:', error);
      
      if (error.response?.status === 500) {
        throw new Error('Google OAuth not configured on server. Please contact administrator.');
      } else {
        throw new Error('Failed to initialize Google authentication service');
      }
    }
  }

  /**
   * Get Google OAuth authorization URL
   * 
   * Returns the URL to redirect users to Google's consent screen.
   * 
   * Validates: Requirements 1.3 - Redirect to Google OAuth consent screen
   */
  public async getAuthorizationUrl(): Promise<string> {
    try {
      if (!this.isInitialized) {
        await this.initializeGoogleAuth();
      }

      const response = await this.withRetry(
        () => api.get<GoogleAuthUrlResponse>('/auth/google/url'),
        3,
        1500
      );
      return response.data.authorization_url;
    } catch (error: any) {
      console.error('Error getting Google authorization URL:', error);
      
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      } else {
        throw new Error('Failed to get Google authorization URL');
      }
    }
  }

  /**
   * Initiate Google sign-in process
   * 
   * Redirects user to Google OAuth consent screen.
   * 
   * Validates: Requirements 1.3 - Google SSO button redirects to OAuth consent screen
   */
  public async signInWithGoogle(): Promise<void> {
    try {
      const authUrl = await this.getAuthorizationUrl();
      
      // Store current location for post-auth redirect
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('pre_auth_path', currentPath);
      
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (error: any) {
      console.error('Error initiating Google sign-in:', error);
      throw error;
    }
  }

  /**
   * Handle Google OAuth callback
   * 
   * Processes the authorization code returned from Google OAuth callback.
   * 
   * @param code - Authorization code from Google OAuth callback
   * @returns Promise<GoogleAuthResponse> - Authentication response with user data and JWT token
   * 
   * Validates: Requirements 1.4, 1.5 - Create/update user account and generate JWT token
   */
  public async handleGoogleCallback(code: string): Promise<GoogleAuthResponse> {
    try {
      if (!code) {
        throw new Error('Authorization code is required');
      }

      // Exchange authorization code for tokens via backend
      const response = await api.post<GoogleAuthResponse>('/auth/google/callback', {
        code: code
      });

      const authData = response.data;

      // Store authentication data
      localStorage.setItem('auth_token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));

      // Set default authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;

      console.log('Google authentication successful:', authData.user.email);
      return authData;

    } catch (error: any) {
      console.error('Google callback error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        throw new Error(detail || 'Your email is not authorized for this application. Please contact administrator.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid authorization code. Please try signing in again.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      } else {
        throw new Error('Google authentication failed. Please try again.');
      }
    }
  }

  /**
   * Direct Google login with authorization code
   * 
   * Alternative method for Google authentication that accepts authorization code directly.
   * 
   * @param code - Authorization code from Google OAuth
   * @returns Promise<GoogleAuthResponse> - Authentication response with user data and JWT token
   * 
   * Validates: Requirements 1.3, 1.4, 1.5 - Complete OAuth flow and session management
   */
  public async loginWithGoogle(code: string): Promise<GoogleAuthResponse> {
    try {
      if (!code) {
        throw new Error('Authorization code is required');
      }

      // Direct login via backend
      const response = await api.post<GoogleAuthResponse>('/auth/google/login', {
        code: code
      });

      const authData = response.data;

      // Store authentication data
      localStorage.setItem('auth_token', authData.access_token);
      localStorage.setItem('user', JSON.stringify(authData.user));

      // Set default authorization header for future requests
      api.defaults.headers.common['Authorization'] = `Bearer ${authData.access_token}`;

      console.log('Direct Google login successful:', authData.user.email);
      return authData;

    } catch (error: any) {
      console.error('Direct Google login error:', error);
      
      // Handle specific error cases
      if (error.response?.status === 403) {
        const detail = error.response?.data?.detail;
        throw new Error(detail || 'Your email is not authorized for this application. Please contact administrator.');
      } else if (error.response?.status === 400) {
        throw new Error('Invalid authorization code. Please try signing in again.');
      } else if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      } else {
        throw new Error('Google authentication failed. Please try again.');
      }
    }
  }

  /**
   * Check if user has valid Google session
   * 
   * Validates stored authentication token and user data.
   * 
   * Validates: Requirements 1.6 - Automatic authentication with valid Google session
   */
  public hasValidSession(): boolean {
    try {
      const token = localStorage.getItem('auth_token');
      const userStr = localStorage.getItem('user');

      if (!token || !userStr) {
        return false;
      }

      const user = JSON.parse(userStr);
      
      // Check if user is Google authenticated
      if (user.auth_provider === 'google' && user.google_id) {
        // Set authorization header if valid session exists
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        return true;
      }

      return false;
    } catch (error) {
      console.error('Error checking session validity:', error);
      return false;
    }
  }

  /**
   * Get current authenticated user
   * 
   * Returns the currently authenticated user data from localStorage.
   */
  public getCurrentUser(): any | null {
    try {
      const userStr = localStorage.getItem('user');
      return userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('Error getting current user:', error);
      return null;
    }
  }

  /**
   * Logout from Google session
   * 
   * Invalidates local session and provides Google logout URL.
   * 
   * Validates: Requirements 1.8 - Session invalidation
   */
  public async logout(): Promise<string> {
    try {
      // Get Google logout URL from backend
      const response = await api.post<GoogleLogoutResponse>('/auth/google/logout');
      
      // Clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('pre_auth_path');
      
      // Remove authorization header
      delete api.defaults.headers.common['Authorization'];
      
      console.log('Google logout initiated');
      return response.data.logout_url;
      
    } catch (error: any) {
      console.error('Error during Google logout:', error);
      
      // Clear local storage even if backend call fails
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      localStorage.removeItem('pre_auth_path');
      delete api.defaults.headers.common['Authorization'];
      
      // Return default Google logout URL as fallback
      return 'https://accounts.google.com/logout';
    }
  }

  /**
   * Clear local session data
   * 
   * Utility method to clear all authentication data from localStorage.
   */
  public clearSession(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('pre_auth_path');
    delete api.defaults.headers.common['Authorization'];
    console.log('Local session cleared');
  }

  /**
   * Handle OAuth callback from URL parameters
   * 
   * Utility method to extract and process OAuth callback parameters from URL.
   * 
   * @param url - Current URL (optional, defaults to window.location.href)
   * @returns Promise<GoogleAuthResponse | null> - Authentication response or null if no callback
   */
  public async handleCallbackFromUrl(url?: string): Promise<GoogleAuthResponse | null> {
    try {
      const currentUrl = url || window.location.href;
      const urlParams = new URLSearchParams(new URL(currentUrl).search);
      
      const code = urlParams.get('code');
      const error = urlParams.get('error');
      
      if (error) {
        throw new Error(`OAuth error: ${error}`);
      }
      
      if (code) {
        // Clean up URL by removing OAuth parameters
        const cleanUrl = new URL(currentUrl);
        cleanUrl.searchParams.delete('code');
        cleanUrl.searchParams.delete('state');
        cleanUrl.searchParams.delete('scope');
        
        // Update browser URL without OAuth parameters
        window.history.replaceState({}, document.title, cleanUrl.toString());
        
        // Process the authorization code
        return await this.handleGoogleCallback(code);
      }
      
      return null;
    } catch (error: any) {
      console.error('Error handling OAuth callback from URL:', error);
      throw error;
    }
  }

  /**
   * Get post-authentication redirect path
   * 
   * Returns the path user was on before authentication, or default path.
   */
  public getPostAuthRedirectPath(): string {
    const savedPath = localStorage.getItem('pre_auth_path');
    localStorage.removeItem('pre_auth_path');
    return savedPath || '/';
  }

  /**
   * Check if Google OAuth is configured
   * 
   * Validates if Google OAuth client ID is configured in environment.
   */
  public isGoogleOAuthConfigured(): boolean {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    return !!(clientId && clientId !== 'your-google-client-id.apps.googleusercontent.com');
  }
}

// Export singleton instance
export const googleAuthService = GoogleAuthService.getInstance();
export default googleAuthService;